// Tarifs — page publique (indexable).
// User instruction: no invented prices, no fake customer testimonials.

import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import SEOHead from "../SEOHead";
import { seoConfig, jsonLdBreadcrumb, jsonLdFAQ } from "../seoConfig";

const P = seoConfig.pages.tarifs;

const FAQ = [
  {
    question: "Pourquoi ne pas afficher directement une grille tarifaire ?",
    answer:
      "WYVIO est déployée dans plusieurs équipes en parallèle. Nous finalisons actuellement la structure tarifaire pour qu'elle reflète honnêtement la valeur apportée. Contactez-nous pour un accès prioritaire.",
  },
  {
    question: "WYVIO remplace-t-elle mes outils actuels ?",
    answer:
      "Non. WYVIO centralise la relation client — chaque échange, document et étape — et cohabite naturellement avec vos outils existants.",
  },
  {
    question: "Combien de temps pour déployer WYVIO dans mon équipe ?",
    answer:
      "Un workflow standard (vente directe, dépôt-vente, occasion) est prêt à l'emploi. La personnalisation des étapes et des messages se fait en quelques minutes depuis les paramètres.",
  },
  {
    question: "Le client doit-il installer une application ?",
    answer:
      "Non. Le portail client fonctionne sur navigateur, y compris sur mobile. Un lien magique 24h à usage unique lui est envoyé par email.",
  },
];

const Tarifs = () => {
  return (
    <>
      <SEOHead
        title={P.title}
        description={P.description}
        keywords={P.keywords}
        path={P.path}
        jsonLd={[
          jsonLdBreadcrumb([
            { name: "Accueil", path: "/" },
            { name: "Tarifs", path: P.path },
          ]),
          jsonLdFAQ(FAQ),
        ]}
      />

      {/* Hero */}
      <section className="mx-auto max-w-4xl px-6 lg:px-10 pt-20 pb-16 lg:pt-28 text-center">
        <p className="text-[12px] font-semibold uppercase tracking-widest text-[#6B7280]">
          Tarifs
        </p>
        <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-[-0.02em] text-[#111]">
          Une offre transparente,<br />bâtie avec les premières équipes.
        </h1>
        <p className="mt-6 text-[15px] lg:text-[16px] leading-relaxed text-[#6B7280]">
          Nous finalisons actuellement la grille tarifaire de WYVIO.<br />
          Contactez-nous : nous prenons le temps de comprendre votre besoin
          avant de vous proposer un accès.
        </p>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/contact"
            data-testid="pricing-cta-contact"
            className="h-12 inline-flex items-center gap-2 rounded-full bg-[#111] text-white text-[14px] font-medium px-6 hover:bg-black transition"
          >
            Prendre contact
            <ArrowRight size={16} strokeWidth={2.25} />
          </Link>
          <Link
            to="/contact"
            data-testid="pricing-cta-mail"
            className="h-12 inline-flex items-center gap-2 rounded-full border border-black/10 text-[#111] text-[14px] font-medium px-6 hover:bg-[#F7F8FA] transition"
          >
            <Mail size={16} strokeWidth={2} />
            Nous écrire
          </Link>
        </div>
      </section>

      {/* What's included preview */}
      <section className="mx-auto max-w-6xl px-6 lg:px-10 py-16 border-t border-black/5">
        <h2 className="text-2xl font-semibold tracking-[-0.02em] text-[#111] max-w-xl">
          Ce que comprend un déploiement WYVIO.
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            {
              title: "Plateforme complète",
              points: [
                "Workflows configurables illimités",
                "Media Hub unifié (documents, photos, vidéos)",
                "Communications automatiques et manuelles",
                "Portail client sécurisé",
                "Centre de pilotage équipe",
              ],
            },
            {
              title: "Accompagnement de mise en route",
              points: [
                "Configuration des workflows adaptés à vos ventes",
                "Import initial de vos dossiers en cours (optionnel)",
                "Formation des équipes",
                "Support pendant la phase de déploiement",
              ],
            },
          ].map((c) => (
            <div key={c.title} className="rounded-2xl border border-black/5 p-8 bg-white">
              <p className="text-[12px] font-semibold uppercase tracking-widest text-[#6B7280]">
                {c.title}
              </p>
              <ul className="mt-5 space-y-2.5">
                {c.points.map((p) => (
                  <li key={p} className="flex gap-3 text-[14px] text-[#111]">
                    <span className="text-[#8FB600]">→</span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section
        data-testid="pricing-faq"
        className="mx-auto max-w-3xl px-6 lg:px-10 py-24 border-t border-black/5"
      >
        <h2 className="text-2xl font-semibold tracking-[-0.02em] text-[#111]">Questions fréquentes</h2>
        <div className="mt-10 space-y-6">
          {FAQ.map((q) => (
            <details
              key={q.question}
              className="group rounded-2xl border border-black/5 bg-white px-6 py-5 open:bg-[#F7F8FA]"
            >
              <summary className="cursor-pointer text-[15px] font-semibold text-[#111] list-none flex items-center justify-between">
                {q.question}
                <span className="text-[#6B7280] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-[14px] text-[#6B7280] leading-relaxed">{q.answer}</p>
            </details>
          ))}
        </div>
      </section>
    </>
  );
};

export default Tarifs;
